import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, Copy, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DATE_TIME_FORMAT, formatDateSafe } from '../../utils/dateUtils';
import { resolveEvidenceJump } from './evidenceNavigation';
import { EVIDENCE_KINDS } from '../../types/evidence';
import type { EvidenceRef } from '../../types/evidence';

interface EvidenceRefRowProps {
  evidenceRef: EvidenceRef;
  onJump?: (evidenceRef: EvidenceRef) => void;
}

/**
 * What a reviewer needs to re-find the source by hand when the jump is blocked.
 *
 * `contentHash` is deliberately NOT included (and #1198's read view withholds it anyway
 * for a floored ref). It is a digest over low-entropy identifiers — for the DRBFM path
 * `{document_id, category, page_number, image_id}` (`application/evidence/legacy_adapters.py:519`)
 * — so handing it to the very viewer whose locator was withheld turns it into an offline
 * oracle for that locator (measured: the page recovered in 417 hashes). The hash is a
 * machine integrity value, not something a human re-finds a document with.
 */
const referenceIdentity = (evidenceRef: EvidenceRef): string => [
  evidenceRef.sourceId ?? '',
  evidenceRef.sourceRevisionId ?? '',
  evidenceRef.retrievedAt,
].join('\n');

const EvidenceRefRow: React.FC<EvidenceRefRowProps> = ({ evidenceRef, onJump }) => {
  const { t, i18n } = useTranslation();
  const reasonId = useId();
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const jump = resolveEvidenceJump(evidenceRef);
  const page = jump.kind === 'link' ? jump.page : null;
  // A kind this build has never heard of must not render its own key as a label.
  const sourceTypeKey = (EVIDENCE_KINDS as readonly string[]).includes(evidenceRef.evidenceKind)
    ? `evidence.sourceType.${evidenceRef.evidenceKind}`
    : 'evidence.sourceType.unknown';

  // The clipboard is absent in a non-secure context, so calling it must degrade rather
  // than throw — this is the only action a blocked row offers.
  const copyReference = () => {
    try {
      Promise.resolve(navigator.clipboard.writeText(referenceIdentity(evidenceRef)))
        .then(() => setCopyState('copied'))
        .catch(() => setCopyState('failed'));
    } catch {
      // `navigator.clipboard` is undefined outside a secure context, so reading it throws
      // synchronously rather than rejecting.
      setCopyState('failed');
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-muted/40 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary" className="revy-cjk">
          {t(sourceTypeKey)}
        </Badge>
        <span className="revy-cjk text-xs text-muted-foreground">{t('evidence.sourceRevision')}</span>
        <span className="revy-figure text-xs">
          {evidenceRef.sourceRevisionId ?? t('evidence.unversioned')}
        </span>
        <span className="revy-cjk text-xs text-muted-foreground">{t('evidence.retrievedAt')}</span>
        <span className="revy-figure text-xs">
          {formatDateSafe(evidenceRef.retrievedAt, DATE_TIME_FORMAT, { t, language: i18n.language })}
        </span>
      </div>

      {jump.kind === 'link' ? (
        <>
          <Link
            to={jump.href}
            onClick={() => onJump?.(evidenceRef)}
            className="revy-cjk inline-flex w-fit items-center gap-1 text-sm underline underline-offset-2"
          >
            <ExternalLink className="size-3.5" strokeWidth={1.9} />
            {page === null ? t('evidence.openDocument') : t('evidence.openPage', { page })}
          </Link>
          {page === null && (
            <p className="revy-cjk text-xs leading-[1.6] text-muted-foreground">
              {t('evidence.pageUnknown')}
            </p>
          )}
        </>
      ) : (
        <>
          {/* Visible text, not a tooltip: a Radix tooltip never opens on touch and is
              unreachable on a disabled trigger, so the reason would be lost. */}
          <p id={reasonId} className="revy-cjk text-xs leading-[1.6] text-muted-foreground">
            {t(`evidence.reason.${jump.reason}`)}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-describedby={reasonId}
            onClick={copyReference}
            className="revy-cjk w-fit gap-1"
          >
            {copyState === 'copied'
              ? <Check className="size-3.5" strokeWidth={1.9} />
              : <Copy className="size-3.5" strokeWidth={1.9} />}
            {t('evidence.copyReference')}
          </Button>
          {copyState !== 'idle' && (
            <p role="status" className="revy-cjk text-xs leading-[1.6] text-muted-foreground">
              {t(copyState === 'copied' ? 'evidence.copied' : 'evidence.copyFailed')}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default EvidenceRefRow;
